<template>
  <div>
    <b-container class="py-3">
      <b-row>
        <b-col>
          <b-alert v-if="inactiveSubscription" show class="mb-3" variant="danger">
            <h4>Expired Subscription</h4>
            <p>
              Your subscription has expired. You will not be able to invite new clients until you <b-link to="/subscribe">renew your subscription</b-link>.
            </p>
            <hr />
            <b-button to="/subscriber/manageSubscription" variant="primary">Manage My Subscription</b-button>
          </b-alert>

          <b-alert v-if="itemCount == 0" show dismissible class="mb-3" variant="success">
            <h4>Getting Started</h4>
            <p>
              To get started, add a new client. You provide the client's name, email address, and phone number, and we'll send them an email and/or an SMS text message with a
              link to a survey. You can also choose additional assessments to present to the client.
            </p>
            <hr />
            <b-button to="/subscriber/client/add" variant="primary">Add New Client</b-button>
          </b-alert>

          <b-alert show v-if="!gotCheckouts" class="mb-3" variant="warning">
            Proctoring offline clients is not currently enabled. Try refreshing your browser to enable this functionality.
          </b-alert>

          <div v-if="gotCheckouts">
            <page-title title="My Offline Clients" />

            <b-alert show v-if="checkouts.length == 0" class="mb-3" variant="info">
              You do not have any offline clients. When you check out a client for offline access, their questionnaire is stored locally on your device. You can 
              then proctor the questionnaire to them without having an internet connection. Once you are able to reconnect to the internet, the questionnaire will
              be synchroized back to our server, and you'll be able to download a mitigation report.
            </b-alert>

            <b-alert show v-if="checkouts.length > 0" class="mb-3" variant="info">
              You can proctor questionnaires to the offline clients listed below without being connected to the internet. Until they have been checked in, all data
              from their questionnaires is stored locally on your device. Once you check them in, the data will be synchronized and you can download a mitigation
              report.
            </b-alert>

            <b-table-simple striped v-if="checkouts.length > 0">
              <b-thead>
                <b-tr>
                  <b-th>Client Name</b-th>
                  <b-th>Actions</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="checkout in checkouts" v-bind:key="checkout.clientId">
                  <td>{{ checkout.displayName }}</td>
                  <td>
                    <b-button-group>
                      <b-button :href="checkout.shortCodeUrl">Proctor Questionnaire</b-button>
                      <b-button :to="'/subscriber/client/' + checkout.clientId + '/checkIn'">Check In</b-button>
                    </b-button-group>
                  </td>
                </b-tr>
              </b-tbody>
            </b-table-simple>

            <!-- <b-alert show v-for="checkout in checkouts" v-bind:key="checkout.clientId" variant="info">
              <div class="mb-3">
                {{ checkout.displayName }} is currently checked out.
              </div>
              <b-button-group>
                <b-button :href="checkout.shortCodeUrl">Proctor Questionnaire</b-button>
                <b-button :to="'/subscriber/client/' + checkout.clientId + '/checkIn'">Check In</b-button>
              </b-button-group>
            </b-alert> -->

            <hr />
          </div>

          <span class="float-right mt-3">
            <b-button to="/subscriber/client/add" :disabled="inactiveSubscription">Add New Client</b-button>
          </span>
          <page-title title="My Clients" />
          <hr />

          <filtered-table :settings="tableSettings" @rowClicked="onRowClicked" @itemsLoaded="onItemsLoaded">
          </filtered-table>

        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import AuthMixin from '../Mixins/AuthMixin.vue';
import axios from "axios";

export default {
  name: "SubscriberDashboard",
  mixins: [AuthMixin],
  props: {},
  data() {
    return {
      checkouts: null,
      gotCheckouts: false,
      subscriptionUser: null,
      inactiveSubscription: false,
      itemCount: -1,
      tableSettings: {
        endpoint: '/api/form',
        includes: [
          'client',
          'client.applicationUser'
        ],
        defaultSortColumn: 'created',
        defaultSortOrder: 'desc',
        columns: [
          {
            key: 'id',
            name: 'ID',
            visible: false,
            sortable: true,
            filter: 'text'
          },
          {
            key: 'client.applicationUser.lastName',
            name: 'Last Name',
            visible: true,
            sortable: true,
            filter: 'text'
          },
          {
            key: 'client.applicationUser.firstName',
            name: 'First Name',
            visible: true,
            sortable: true,
            filter: 'text'
          },
          {
            key: 'client.applicationUser.email',
            name: 'Email',
            visible: false,
            sortable: true,
            filter: 'text'
          },
          {
            key: 'client.applicationUser.phoneNumber',
            name: 'Phone Number',
            visible: false,
            sortable: true,
            filter: 'text'
          },
          {
            key: 'client.isProctored',
            name: 'Proctored',
            visible: true,
            sortable: false,
            filter: 'none',
            render: (v, row) => {
              if (v)
                return 'Proctored';
              else
                return 'Not Proctored';
            }
          },
          {
            key: 'formStatus',
            name: 'Status',
            visible: true,
            sortable: false,
            filter: 'select',
            selectOptions: [
              { text: '', value: null },
              { text: 'Created', value: 'Created' },
              { text: 'Started', value: 'Started' },
              { text: 'Completed', value: 'Completed' }
            ]
          },
          {
            key: 'created',
            name: 'Created',
            visible: false,
            sortable: true
          }
        ],
        getDefaultFilter: function () {
          return 'originalFormId=null';
        },
      }
    }
  },
  computed: {
  },
  methods: {
    getCheckouts: function () {
      navigator.serviceWorker.controller.postMessage({
        action: 'getCheckouts'
      });
    },
    onRowClicked: function (item) {
      this.$router.push('/subscriber/client/' + item.client.id); 
    },
    onItemsLoaded: function (rawItems) {
      this.itemCount = rawItems.length;
    },
    loadSubscriptionUser: function () {
      axios({
        url: '/api/subscriptionUser?includes=subscription&filter=' + encodeURIComponent('applicationUserId="' + this.$root.$data.authenticatedUser.id + '"'),
        method: 'GET'
      }).then((response) => {
        if (!response.data || response.data.length < 1) {
          this.subscriptionUser = null;
          this.inactiveSubscription = true;
        } else {
          this.subscriptionUser = response.data[0];
          this.inactiveSubscription = !this.subscriptionUser.subscription.isActive;
        }
      });

    }
  },
  watch: {
  },
  created() {
  },
  async mounted () {
    this.loadSubscriptionUser();
    this.getCheckouts();

    navigator.serviceWorker.onmessage = msg => {
      if (msg.data.action == 'gotCheckouts') {
        this.checkouts = msg.data.checkouts;
        this.gotCheckouts = true;
      }
    }

  }
};
</script>